import { useState, useEffect, useRef, useCallback } from "react";
import { Textarea } from "../ui/textarea";
import { Check, Loader2 } from "lucide-react";

export function AutoSaveNote({ value, onSave, placeholder = "Add a note..." }) {
  const [text, setText] = useState(value || "");
  const [status, setStatus] = useState(null); // null | "saving" | "saved"
  const timerRef = useRef(null);
  const initialRef = useRef(value || "");

  // Sync if external value changes (e.g. after refetch)
  useEffect(() => {
    setText(value || "");
    initialRef.current = value || "";
  }, [value]);

  const save = useCallback(async (newText) => {
    if (newText === initialRef.current) return;
    setStatus("saving");
    try {
      await onSave(newText);
      initialRef.current = newText;
      setStatus("saved");
      setTimeout(() => setStatus(null), 1500);
    } catch {
      setStatus(null);
    }
  }, [onSave]);

  const handleChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    setStatus(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(newText), 1000);
  };

  // Save on blur immediately
  const handleBlur = () => {
    clearTimeout(timerRef.current);
    save(text);
  };

  // Cleanup timer
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <div className="relative">
      <Textarea
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="text-sm min-h-[50px] pr-8"
        rows={2}
      />
      {status && (
        <div className="absolute top-2 right-2">
          {status === "saving" && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
          {status === "saved" && <Check className="w-3.5 h-3.5 text-green-500" />}
        </div>
      )}
    </div>
  );
}
